import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Users, 
  ClipboardList, 
  JapaneseYen, 
  AlertTriangle,
  Calendar,
  ArrowRight,
  FileText,
  BookOpen,
  ShieldAlert,
  UserCircle,
  Building2,
  CreditCard
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'system_admin';
  const [stats, setStats] = useState({
    clients_count: 0,
    records_count: 0,
    current_month_billing: 0,
    draft_billings_count: 0,
    alerts: []
  });
  const [recentRecords, setRecentRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, recordsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/support-records`)
        ]);
        setStats(statsRes.data);
        setRecentRecords(recordsRes.data.slice(0, 5));
      } catch (error) {
        console.error('ダッシュボードデータの取得に失敗しました', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
  };

  const currentMonth = new Date().toLocaleDateString('ja-JP', { year: 'numeric', month: 'long' });

  // 全メニュー項目
  const menuItems = [
    { to: '/clients', icon: Users, label: '利用者管理', desc: `${stats.clients_count}名登録`, color: 'bg-blue-50 text-blue-600', border: 'hover:border-blue-300' },
    { to: '/records', icon: ClipboardList, label: '支援記録', desc: `${stats.records_count}件`, color: 'bg-emerald-50 text-emerald-600', border: 'hover:border-emerald-300' },
    { to: '/billing', icon: JapaneseYen, label: '請求管理', desc: `下書き${stats.draft_billings_count}件`, color: 'bg-amber-50 text-amber-600', border: 'hover:border-amber-300' },
    { to: '/documents', icon: FileText, label: '書類管理', desc: '書類・受給者証', color: 'bg-purple-50 text-purple-600', border: 'hover:border-purple-300' },
    { to: '/support-plans', icon: BookOpen, label: '個別支援計画', desc: '計画作成・管理', color: 'bg-cyan-50 text-cyan-600', border: 'hover:border-cyan-300' },
    { to: '/incidents', icon: ShieldAlert, label: 'インシデント管理', desc: '事故・ヒヤリハット', color: 'bg-red-50 text-red-600', border: 'hover:border-red-300' },
    { to: '/staff', icon: UserCircle, label: '職員管理', desc: 'スタッフ情報', color: 'bg-indigo-50 text-indigo-600', border: 'hover:border-indigo-300' },
    { to: '/shifts', icon: Calendar, label: 'シフト管理', desc: 'シフト作成・確認', color: 'bg-teal-50 text-teal-600', border: 'hover:border-teal-300' },
    { to: '/templates', icon: FileText, label: 'PDFテンプレート', desc: '帳票テンプレート', color: 'bg-orange-50 text-orange-600', border: 'hover:border-orange-300' },
    { to: '/subscription', icon: CreditCard, label: 'プラン・課金', desc: 'サブスクリプション', color: 'bg-pink-50 text-pink-600', border: 'hover:border-pink-300' },
  ];

  // 管理者用項目
  if (isAdmin) {
    menuItems.splice(0, 0,
      { to: '/facilities', icon: Building2, label: '事業所管理', desc: '事業所一覧', color: 'bg-slate-50 text-slate-600', border: 'hover:border-slate-300' },
      { to: '/users', icon: Users, label: 'ユーザー管理', desc: 'アカウント管理', color: 'bg-gray-50 text-gray-600', border: 'hover:border-gray-300' },
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* ヘッダー */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          ダッシュボード
        </h1>
        <p className="text-muted-foreground mt-1">
          {currentMonth}の概要
        </p>
      </div>

      {/* 統計カード */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-surface cursor-pointer transition-all hover:shadow-md hover:border-blue-300" onClick={() => navigate('/clients')} data-testid="stat-card-clients">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">利用者数</CardTitle>
            <Users size={20} className="text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.clients_count}</div>
            <p className="text-xs text-muted-foreground mt-1">登録済み利用者</p>
          </CardContent>
        </Card>

        <Card className="card-surface cursor-pointer transition-all hover:shadow-md hover:border-emerald-300" onClick={() => navigate('/records')} data-testid="stat-card-records">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">支援記録</CardTitle>
            <ClipboardList size={20} className="text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.records_count}</div>
            <p className="text-xs text-muted-foreground mt-1">総記録数</p>
          </CardContent>
        </Card>

        <Card className="card-surface cursor-pointer transition-all hover:shadow-md hover:border-amber-300" onClick={() => navigate('/billing')} data-testid="stat-card-billing">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">今月の請求額</CardTitle>
            <JapaneseYen size={20} className="text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.current_month_billing)}</div>
            <p className="text-xs text-muted-foreground mt-1">下書き: {stats.draft_billings_count}件</p>
          </CardContent>
        </Card>

        <Card className="card-surface cursor-pointer transition-all hover:shadow-md hover:border-red-300" data-testid="stat-card-alerts">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">アラート</CardTitle>
            <AlertTriangle size={20} className="text-red-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.alerts?.length || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">要確認項目</p>
          </CardContent>
        </Card>
      </div>

      {/* 全機能メニュー */}
      <div>
        <h2 className="text-lg font-semibold mb-4">機能メニュー</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {menuItems.map((item) => (
            <Card
              key={item.to}
              className={`card-surface cursor-pointer transition-all hover:shadow-md border-2 border-transparent ${item.border}`}
              onClick={() => navigate(item.to)}
              data-testid={`menu-card-${item.to.replace('/', '')}`}
            >
              <CardContent className="p-4 flex flex-col items-center text-center gap-2">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${item.color}`}>
                  <item.icon size={24} />
                </div>
                <span className="font-medium text-sm">{item.label}</span>
                <span className="text-xs text-muted-foreground">{item.desc}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* 最近の支援記録 + クイックアクション */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="card-surface lg:col-span-2" data-testid="recent-records-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">最近の支援記録</CardTitle>
            <Link to="/records" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="view-all-records-link">
              すべて表示 <ArrowRight size={14} />
            </Link>
          </CardHeader>
          <CardContent>
            {recentRecords.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <ClipboardList size={48} className="mx-auto mb-2 opacity-50" />
                <p>支援記録がありません</p>
                <Link to="/records/new" className="text-primary hover:underline text-sm mt-2 inline-block">記録を作成する</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentRecords.map((record) => (
                  <Link key={record.id} to="/records" className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Calendar size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{record.client_name}</p>
                        <p className="text-xs text-muted-foreground">{record.service_date} | {record.service_type}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{record.duration_minutes}分</p>
                      <p className="text-xs text-muted-foreground">{record.staff_name}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="card-surface" data-testid="quick-actions-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">クイックアクション</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/clients/new" className="flex items-center gap-3 p-3 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors" data-testid="quick-action-new-client">
              <Users size={20} /><span className="font-medium">利用者を登録</span>
            </Link>
            <Link to="/records/new" className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-secondary transition-colors" data-testid="quick-action-new-record">
              <ClipboardList size={20} className="text-primary" /><span className="font-medium">支援記録を作成</span>
            </Link>
            <Link to="/documents" className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-secondary transition-colors" data-testid="quick-action-documents">
              <FileText size={20} className="text-primary" /><span className="font-medium">書類をアップロード</span>
            </Link>
            <Link to="/billing" className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-secondary transition-colors" data-testid="quick-action-billing">
              <JapaneseYen size={20} className="text-primary" /><span className="font-medium">請求を確認</span>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
